import { useGlobalState } from '../GlobalState';
import '../../assets/css/payment_form.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Spin } from 'antd';
import { costString } from '../../../../utils/cost';
import PaymentFailure from './PaymentFailure';
import useRyftPay from './useRyftPay';
import {
  useCheckPaymentAvailabilityMutation,
  useCreatePaymentSessionMutation,
} from '../../paymentsSlice';
import { defaultErrorNotification } from '../../../../store/proApiSlice';

function formatRyftError(err) {
  return {
    message: 'Payment Failed',
    payment_status: 'Error',
    payment_succeeded: false,
    payment_status_detail: err?.data ?? String(err),
  };
}

function formatCardValidity(validE) {
  if (!validE || validE?.isValid) {
    return null;
  }
  if (validE?.message) {
    return validE.message;
  }
  // Checks ordered to match up with field order in form
  if (!validE?.cardNumberValid) {
    return 'Please enter a valid card number';
  }
  if (!validE?.expirationValid) {
    return 'Please enter a valid expiry date';
  }
  if (!validE?.cvcValid) {
    return 'Please enter a valid CVC/CVV number';
  }
  if (!validE?.nameOnCardValid) {
    return 'Please enter a valid cardholder name';
  }
}

function RyftPaymentForm({
  idField = { name: 'booking_public_id', value: 0 },
  amount,
  onPaymentSuccess,
  onPaymentFailure,
  enableSaveCard = false,
  enableWalletPayments = false,
  enableStoredCardsUse = false,
}) {
  const [paymentSessionData, setPaymentSessionData] = useState(null);
  const { Ryft, ready: ryftLoaded } = useRyftPay({
    noInit: enableWalletPayments && !paymentSessionData?.client_secret, // Wait for client secret before initialising
    clientSecret: paymentSessionData?.client_secret, // Known at this point for payment retries or when Apple Pay is enabled
    customerPaymentMethods: {
      rawJson: JSON.stringify(paymentSessionData?.stored_cards_response),
      allowStorage: enableSaveCard,
    },
    applePay: enableWalletPayments
      ? {
          merchantName: 'swimbooker',
          merchantCountryCode: 'GB',
          buttonConfiguration: {
            width: '100%',
            height: '3rem',
            borderRadius: 6,
            color: 'black',
            type: 'plain',
          },
        }
      : undefined,
    googlePay: enableWalletPayments
      ? {
          merchantName: 'swimbooker',
          merchantCountryCode: 'GB',
          buttonConfiguration: {
            width: '100%',
            height: '3rem',
            borderRadius: 6,
            color: 'black',
            type: 'plain',
          },
        }
      : undefined,
  });

  const [, setPaymentSuccessRedirect] = useGlobalState(
    'paymentSuccessRedirect'
  );
  const [, setBookingTrialIssued] = useGlobalState('bookingTrialIssued');

  const [localError, setLocalError] = useState(null);
  // Enables errors to stay hidden during first form input attempt (until button is clicked)
  const [showValidityError, setShowValidityError] = useState(false);
  const [cardValidity, setCardValidity] = useState(null);

  const [createPaymentSession, { isFetching: creatingSession }] =
    useCreatePaymentSessionMutation();
  const [checkPaymentAvailable] = useCheckPaymentAvailabilityMutation();
  const [isPrefetching, setIsPrefetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Handle Ryft payment result (calls onPaymentSuccess/onPaymentFailure) for a valid payment session object
   * @type {(function(paymentSessionData|*): Promise<void>)}
   */
  const handleRyftPaymentResult = useCallback(
    async (paymentSession) => {
      try {
        if (
          paymentSession.status === 'Approved' ||
          paymentSession.status === 'Captured'
        ) {
          onPaymentSuccess();
          return;
        }

        let userFacingError = null;
        if (paymentSession?.lastError) {
          console.error('Last Error: ', paymentSession.lastError);
          userFacingError = formatRyftError(
            Ryft.getUserFacingErrorMessage(paymentSession.lastError)
          );
        }
        if (!userFacingError) {
          // Case where status is not successful, but there is no lastError field
          // Shouldn't occur based on API documentation, but is here as a fallback
          userFacingError = {
            message: 'An unexpected error occurred with the payment provider.',
            payment_succeeded: false,
            payment_status: 'An error occurred',
            payment_status_detail: 'No further information available.',
            server_error_detail: `Session ID: ${paymentSession?.id}, Status: ${
              paymentSession.status
            }, Code: ${paymentSession?.lastError ?? 'Not Available'}`,
          };
        }

        setLocalError(userFacingError);
        onPaymentFailure();
      } catch (err) {
        /* Fallback for any unhandled errors */
        console.error('Unexpected error in handlePaymentResult', err);
        defaultErrorNotification(err);
      }
    },
    [Ryft, onPaymentFailure, onPaymentSuccess]
  );

  /**
   * Set up Ryft Event Handlers
   */
  useEffect(() => {
    if (!Ryft) {
      // Ryft.js hasn't loaded yet
      return;
    }

    Ryft.addEventHandler('cardValidationChanged', (e) => {
      // Card details have been edited and form validity re-checked
      setCardValidity(e);
    });

    Ryft.addEventHandler('paymentMethodSelectionChanged', (e) => {
      // Used for stored payment methods
      if (!e.paymentMethod) {
        setCardValidity({
          isValid: false,
          message: 'Please select a payment method to continue.',
        });
      } else {
        setCardValidity({ isValid: true });
      }
      // TODO we recommend also checking the validation property and displaying a user-friendly message if the stored
      //      payment method is no longer valid (e.g. expired)
    });

    Ryft.addEventHandler('walletButtonsReady', (e) => {
      console.log('Wallet Buttons Ready', e);
    });

    Ryft.addEventHandler('walletPaymentSessionResult', (e) => {
      console.log('Wallet Results', e);
      setIsSubmitting(true);
      handleRyftPaymentResult(e.paymentSession).finally(() =>
        setIsSubmitting(false)
      );
    });
  }, [Ryft, handleRyftPaymentResult, onPaymentFailure, onPaymentSuccess]);

  /**
   * Get the current payment session, or create a new one if it doesn't exist yet in state.
   * @type {function(): Promise<paymentSessionData|*>}
   */
  const getPaymentSession = useCallback(async () => {
    const data = {
      [idField.name]: idField.value,
      return_url: null,
      include_stored_payment_methods: enableStoredCardsUse,
    };
    let localSessionData = paymentSessionData;
    if (!localSessionData) {
      setPaymentSuccessRedirect(null);
      setBookingTrialIssued(false);
      const response = await createPaymentSession(data).unwrap();
      setPaymentSuccessRedirect(response?.redirect);
      setPaymentSessionData(response);
      if (response?.trial_issued !== undefined) {
        setBookingTrialIssued(Boolean(response.trial_issued));
      }
      localSessionData = response;
    } else {
      const { ready_for_payment = false } =
        await checkPaymentAvailable(data).unwrap();
      if (!ready_for_payment) {
        // Propagate error to top level catch handler
        throw {
          data: {
            detail: {
              message: 'Payment Timeout.',
              payment_succeeded: false,
              payment_status:
                'This booking slot is no longer available for payment',
              payment_status_detail:
                'It may have expired or been cancelled by an admin. Please try again.',
            },
          },
        };
      }
    }

    return localSessionData;
  }, [
    checkPaymentAvailable,
    createPaymentSession,
    enableStoredCardsUse,
    idField.name,
    idField.value,
    paymentSessionData,
    setBookingTrialIssued,
    setPaymentSuccessRedirect,
  ]);

  /**
   * Pre-fetch checkout session if wallet payments are enabled
   */
  useEffect(() => {
    if (!enableWalletPayments) {
      return; // In standard case -- defer payment session creation until the user has filled out the form and clicked "Pay"
    }

    if (isPrefetching || creatingSession || paymentSessionData) {
      return; // Don't trigger multiple requests
    }

    if (localError) {
      return; // Don't re-try whilst error page is visible
    }

    setIsPrefetching(true);

    console.log('Prefetching payment session', {
      isPrefetching,
      creatingSession,
      paymentSessionData,
    });
    getPaymentSession()
      .catch((err) => {
        try {
          console.log('Caught: ', err);
          setLocalError(
            err?.data?.detail ??
              err?.response?.data?.detail ??
              formatRyftError(err)
          );
          onPaymentFailure();
        } catch (err2) {
          console.error('Unexpected error', err2);
          defaultErrorNotification(
            err2
          ); /* Fallback catch for any errors in above code */
        }
      })
      .finally(() => setIsPrefetching(false));
  }, [
    creatingSession,
    enableWalletPayments,
    getPaymentSession,
    isPrefetching,
    localError,
    onPaymentFailure,
    paymentSessionData,
    setBookingTrialIssued,
    setPaymentSuccessRedirect,
  ]);

  /**
   * Handler for when the user clicks the "Pay" button after filling out the payment form
   * @type {(function(SubmitEvent<HTMLFormElement>): Promise<void>)}
   */
  const confirmPayment = useCallback(
    async (e) => {
      // Prevent default form submission (page refresh) - defer to Ryft instead
      e.preventDefault();
      if (!Ryft) {
        // Ryft.js hasn't loaded yet
        return;
      }

      if (!cardValidity?.isValid) {
        return; // Handled here (rather than in button.disabled) to allow for error messages to be shown upon click
      }

      setIsSubmitting(true);
      setLocalError(null);

      try {
        const localSessionData = await getPaymentSession();
        console.log('Attempting Payment', localSessionData);
        const paymentSession = await Ryft.attemptPayment({
          clientSecret: localSessionData.client_secret,
          accountId: localSessionData?.account_id,
        });
        await handleRyftPaymentResult(paymentSession);
      } catch (err) {
        try {
          console.log('Caught: ', err);
          setLocalError(
            err?.data?.detail ??
              err?.response?.data?.detail ??
              formatRyftError(err)
          );
          onPaymentFailure();
        } catch (err2) {
          console.error('Unexpected error', err2);
          defaultErrorNotification(
            err2
          ); /* Fallback catch for any errors in above code */
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      Ryft,
      cardValidity?.isValid,
      getPaymentSession,
      handleRyftPaymentResult,
      onPaymentFailure,
    ]
  );

  const paymentForm = useMemo(
    () => (
      <form onSubmit={confirmPayment} id='ryft-pay-form'>
        {(!ryftLoaded || isPrefetching) && (
          <Spin className={'txt-center w-full'} />
        )}
        {/* Ryft form will be injected here*/}
        <Form.ErrorList
          className='red-400 gRegular mg-bottom-5'
          errors={
            showValidityError && cardValidity
              ? [formatCardValidity(cardValidity)]
              : []
          }
        />
        <Button
          id='ryft-pay-btn'
          className={
            'payment_form__submit sb-blue-bg white w-full gSemiBold fs-20 pd-10 h-full br5 mg-top-10'
          }
          htmlType='submit'
          loading={isSubmitting || creatingSession}
          disabled={
            !Ryft ||
            !ryftLoaded ||
            isPrefetching ||
            isSubmitting ||
            creatingSession
          }
          onClick={() => setShowValidityError(true)}
        >
          Pay {amount ? costString(amount) : '£unknown'}
        </Button>
        <div id='ryft-pay-error'></div>
      </form>
    ),
    [
      confirmPayment,
      ryftLoaded,
      isPrefetching,
      showValidityError,
      cardValidity,
      isSubmitting,
      creatingSession,
      Ryft,
      amount,
    ]
  );

  return (
    <div className='pd-top-10 pd-bottom-20'>
      <div className={localError ? 'hide' : 'show'}>{paymentForm}</div>
      {localError && (
        <PaymentFailure
          errorPayment={localError}
          retryPaymentButton={
            <Button
              className={
                'sb-blue-bg gSemiBold br10 fs-20 white txt-center w-full pd-10 mg-top-10 h-fit antd-link-button-override'
              }
              onClick={() => setLocalError(null)}
            >
              &lt; Back to payment
            </Button>
          }
        />
      )}
    </div>
  );
}

export default RyftPaymentForm;
